
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MoreVertical, Edit2, AlertTriangle, Trash2, XCircle, CheckCircle2, Undo2 } from 'lucide-react';
import { Order } from '../../types';

interface MenuProps {
    order: Order;
    isReturned: boolean;
    isConfirmingDelete: boolean;
    onEdit?: (order: Order) => void;
    onReportIncident?: (order: Order) => void;
    onDeleteClick: (e: React.MouseEvent) => void;
    onCancelOD?: (id: string, mode: 'request' | 'accept' | 'deny') => void;
    onManualComplete?: (orderId: string) => void;
    onRecall?: (orderId: string) => void; // NOVO: Callback para chamar de volta
}

export const OrderMenu: React.FC<MenuProps & { ultraCompact?: boolean }> = ({
    order, isReturned, isConfirmingDelete, onEdit, onReportIncident, onDeleteClick, onCancelOD, onManualComplete, onRecall,
    ultraCompact = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLButtonElement>(null);

    // Alinhamento v3.5: Verifica se o canal suporta handshake de cancelamento via Open Delivery
    const salesChannel = order.salesChannel?.toLowerCase() || '';
    const isOD = salesChannel.includes('ifood') || salesChannel.includes('open delivery');
    const hasPendingODCancel = (order as any).pendingCancellationRequest === true;

    // Status que permitem recolhimento (em mãos do motoboy ou app)
    const isDispatched = order.status === 'dispatched' || order.status === 'dispatched_3rd_party';
    const isAssigned = order.status === 'assigned';
    const isRecallable = isDispatched || isAssigned;

    const handleOpen = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setMenuPos({
                top: rect.bottom + window.scrollY + 5,
                left: rect.right - 192
            });
        }
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const handleScroll = () => setIsOpen(false);
        if (isOpen) {
            window.addEventListener('scroll', handleScroll, true);
        }
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [isOpen]);

    return (
        <div className="relative pointer-events-auto shrink-0">
            <button
                ref={triggerRef}
                type="button"
                onClick={handleOpen}
                aria-label="Abrir menu de ações"
                className={`rounded transition-colors ${ultraCompact ? 'p-0.5' : 'p-1'} ${hasPendingODCancel ? 'bg-sal-crimson-alert text-badge-text-inv animate-pulse' : 'text-text-muted hover:text-text-primary hover:bg-sal-gunmetal-light/10'}`}
            >
                <MoreVertical className={ultraCompact ? "w-3.5 h-3.5" : "w-4 h-4"} />
            </button>

            {isOpen && createPortal(
                <>
                    <div
                        className="fixed inset-0 z-[9998] bg-transparent"
                        onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -5 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        style={{
                            position: 'absolute',
                            top: menuPos.top,
                            left: menuPos.left,
                            width: '192px'
                        }}
                        className="bg-card-bg border border-border-dim rounded-xl shadow-[var(--shadow-lg)] z-[9999] overflow-hidden py-1.5 ring-1 ring-black/10"
                    >
                        {hasPendingODCancel && (
                            <div className="bg-sal-crimson-alert/10 p-3 border-b border-sal-crimson-alert/30 mb-1">
                                <p className="text-xs font-black text-sal-crimson-alert uppercase tracking-widest text-center">Cancelamento iFood!</p>
                                <div className="flex gap-2 mt-2">
                                    <button onClick={(e) => { e.stopPropagation(); onCancelOD?.(order.id, 'accept'); setIsOpen(false); }} className="flex-1 bg-status-ready hover:brightness-110 p-2 rounded text-xs font-black text-badge-text-inv uppercase transition-colors">Aceitar</button>
                                    <button onClick={(e) => { e.stopPropagation(); onCancelOD?.(order.id, 'deny'); setIsOpen(false); }} className="flex-1 bg-sal-crimson-alert hover:brightness-110 p-2 rounded text-xs font-black text-badge-text-inv uppercase transition-colors">Negar</button>
                                </div>
                            </div>
                        )}

                        {isDispatched && onManualComplete && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onManualComplete(order.id); setIsOpen(false); }}
                                className="w-full text-left px-4 py-2.5 text-sm font-black text-status-ready hover:bg-status-ready/10 flex items-center gap-3 transition-colors bg-status-ready/5"
                            >
                                <CheckCircle2 className="w-4 h-4" /> Confirmar Entrega
                            </button>
                        )}

                        {isRecallable && onRecall && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onRecall(order.id); setIsOpen(false); }}
                                className="w-full text-left px-4 py-2.5 text-sm font-black text-sal-burnt-orange hover:bg-sal-burnt-orange/10 flex items-center gap-3 transition-colors"
                            >
                                <Undo2 className="w-4 h-4" /> Recolher p/ Triagem
                            </button>
                        )}

                        <button
                            onClick={(e) => { e.stopPropagation(); if (onEdit) onEdit(order); setIsOpen(false); }}
                            className="w-full text-left px-4 py-2.5 text-sm font-bold text-text-secondary hover:text-text-primary hover:bg-sal-gunmetal-light/10 flex items-center gap-3 transition-colors"
                        >
                            <Edit2 className="w-4 h-4 text-sal-cyber-teal" /> Editar Detalhes
                        </button>

                        {isOD && !isReturned && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onCancelOD?.(order.id, 'request'); setIsOpen(false); }}
                                className="w-full text-left px-4 py-2.5 text-sm font-bold text-sal-crimson-alert hover:bg-sal-crimson-alert/10 flex items-center gap-3 border-t border-border-dim transition-colors"
                            >
                                <XCircle className="w-4 h-4" /> Cancelar no iFood
                            </button>
                        )}

                        {!isReturned && (
                            <button
                                onClick={(e) => { e.stopPropagation(); if (onReportIncident) onReportIncident(order); setIsOpen(false); }}
                                className="w-full text-left px-4 py-2.5 text-sm font-bold text-text-secondary hover:text-text-primary hover:bg-sal-gunmetal-light/10 flex items-center gap-3 transition-colors"
                            >
                                <AlertTriangle className="w-4 h-4 text-sal-burnt-orange" /> Registrar Incidente
                            </button>
                        )}

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteClick(e);
                                if (isConfirmingDelete) setIsOpen(false);
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm font-black text-status-critical hover:bg-status-critical/10 flex items-center gap-3 border-t border-border-dim transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                            {isConfirmingDelete ? "CONFIRMAR REMOÇÃO?" : "Excluir Local"}
                        </button>
                    </motion.div>
                </>,
                document.body
            )}
        </div>
    );
};
